import React, {useEffect, useState} from 'react';
import {Box, Tab, Tabs, Typography} from '@mui/material';
import './EditUser.scss';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import {t} from 'i18next';
import ArrButton from '../../common/arr-button/ArrButton';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {InfoOutline} from '@mui/icons-material';
import FunctionalityTab from './functionality-tab/FunctionalityTab';
import ArrModal from '../../common/arr-modal/ArrModal';
import DetailsTab from './details-tab/DetailsTab';
import {
  useEditUser,
  useSecurityGroups,
  useUser,
  useUserMeta,
} from '../../../hooks/users/useUsers';
import {IProjectItem, SecurityGroupItem} from '../../../api/types/user.type';
import {RoleId, ToastType} from '../../../shared/constants';
import {ISelectItem} from '../../../shared/types';
import {isEmpty, isEqual} from 'lodash';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {FIRST_LAST_NAME_REGEX} from '../../../shared/constants';
interface EditUserProps {
  userId: number;
  projectId?: number;
  entityId?: number;
  showDrawer: boolean;
  handleDrawer: (visible: boolean) => void;
  onUserEdited?: () => void;
}

export interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
export interface TabData {
  id: string;
  label: React.ReactNode;
  content: React.ReactNode;
  enabled?: boolean;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      className="tabpanel"
      {...other}
      data-testid={`edit-user-tab-${index}`}
    >
      {value === index && (
        <div data-testid={`edit-user-child-tab-${index}`}>{children}</div>
      )}
    </div>
  );
}

const EditUser: React.FC<EditUserProps> = ({
  userId,
  projectId,
  entityId,
  showDrawer,
  handleDrawer,
  onUserEdited,
}) => {
  const [initialIds, setInitialIds] = useState({userId, projectId, entityId});
  const {
    data: user,
    isLoading: isLoadingUser,
    refetch,
  } = useUser(initialIds?.userId, initialIds?.projectId, initialIds?.entityId);
  const {data: userMeta, isLoading: isLoadingUserMeta} = useUserMeta();
  const rolesOptions = userMeta?.roles ?? [];
  const roleIdType = typeof user?.role === 'number';
  const {data: securityGRPsOptions = []} = useSecurityGroups(
    Number(user?.role),
    Number(user?.projectID),
    roleIdType && user?.role > 1 && Number.isFinite(user?.projectID)
  );

  const [firstName, setFirstName] = useState('');
  const [isFNameValid, setIsFNameValid] = useState<boolean>(true);
  const [isFNameMaxLengthValid, setIsFNameMaxLengthValid] =
    useState<boolean>(true);
  const [lastName, setLastName] = useState('');
  const [isLNameValid, setIsLNameValid] = useState<boolean>(true);
  const [isLNameMaxLengthValid, setIsLNameMaxLengthValid] =
    useState<boolean>(true);
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [editUserRole, setEditUserRole] = useState('');
  const [selectedRole, setSelectedRole] = useState<ISelectItem | null>(null);
  const [isBillableUser, setIsBillableUser] = useState(false);
  const [isAttorneyProfile, setIsAttorneyProfile] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [securityGroups, setSecurityGroups] = useState<SecurityGroupItem[]>([]);
  const [projectName, setProjectName] = useState<string | IProjectItem>('');
  const [domainName, setDomainName] = useState<string>('');
  const [userRights, setUserRights] = useState<
    Record<string, boolean | 'disabled'>
  >({});
  const [project, setProject] = useState<ISelectItem | null>(null);
  const [saveDisable, setSaveDisable] = useState<boolean>(true);

  const [value, setValue] = useState(0);
  const [toastStatuses, setToastStatuses] = useState<ToastType[]>([]);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const [unsavedChangesModal, setUnsavedChangesModal] =
    useState<boolean>(false);
  const handleCancel = () => {
    if (!saveDisable) {
      setUnsavedChangesModal(true);
    } else {
      handleDrawer(false);
    }
  };
  const MAX_NAME_LENGTH = 50;
  const [touchedFields, setTouchedFields] = useState<{
    fName: boolean;
    lName: boolean;
  }>({
    fName: false,
    lName: false,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const callSecurityApi =
    (selectedRole?.key as number) > 1 &&
    (Number.isFinite(project?.key) ||
      editUserRole === RoleId.ProjectAdministrator);

  const {data: securityGRPsOptionsForEdit = []} = useSecurityGroups(
    Number(selectedRole?.key),
    Number(project?.key) || Number(user?.projectID),
    callSecurityApi
  );

  const handleReset = () => {
    if (!user) return;
    const firstNameValue = user.firstName || '';
    const lastNameValue = user.lastName || '';

    setFirstName(firstNameValue);
    setLastName(lastNameValue);
    setPrimaryEmail(user.emailID || '');
    setEditUserRole(user.role?.toString() || '');
    setIsBillableUser(user.isBillable || false);
    setIsAttorneyProfile(user.isAttorneyProfile || false);
    setIsActive(user.isActive || false);
    setIsLocked(user.isLocked || false);
    setProjectName(user.selectedProject || '');
    setDomainName(user.selectedDomain || '');
    if (firstNameValue) {
      setIsFNameValid(FIRST_LAST_NAME_REGEX.test(firstNameValue));
      setIsFNameMaxLengthValid(firstNameValue.length <= MAX_NAME_LENGTH);
    } else {
      setIsFNameValid(false);
      setIsFNameMaxLengthValid(true);
    }

    if (lastNameValue) {
      setIsLNameValid(FIRST_LAST_NAME_REGEX.test(lastNameValue));
      setIsLNameMaxLengthValid(lastNameValue.length <= MAX_NAME_LENGTH);
    } else {
      setIsLNameValid(false);
      setIsLNameMaxLengthValid(true);
    }
    setTouchedFields({fName: false, lName: false});
    setFieldErrors({});
    const selectedGroups = securityGRPsOptions.filter((grp) =>
      user.securityGroupIDs?.includes(grp.key.toString())
    );
    setSecurityGroups(selectedGroups);

    setUserRights(user.userRights);
    setSaveDisable(true);
  };
  const handleFunctionalityChange = (
    updatedRights: Record<string, boolean | 'disabled'>
  ) => {
    setUserRights(updatedRights);
    setSaveDisable(false);
  };
  useEffect(() => {
    const editedRole = selectedRole?.key || 0;
    const role = user?.role?.toString() || 0;
    if (userRights) {
      const rights = {...userRights};
      if (role == editedRole) {
        if (role !== RoleId.Reviewer) {
          if (!user?.genAiEnabled) {
            rights.canGenAi = 'disabled';
            rights.canManageIndices = 'disabled';
            rights.canManageProtocols = 'disabled';
            rights.canAIReview = 'disabled';
            rights.canAIBatchSummarize = 'disabled';
            rights.canAIInvestigate = 'disabled';
          } else {
            if (!user?.aiBatchSummarize) {
              rights.canAIBatchSummarize = 'disabled';
            }
            if (!user?.aiReview) {
              rights.canAIReview = 'disabled';
              rights.canManageProtocols = 'disabled';
            }
            if (!user?.aiInvestgate) {
              rights.canAIInvestigate = 'disabled';
              rights.canManageIndices = 'disabled';
            }
          }
        }
        if (role == RoleId.DomainAdministrator) {
          rights.canAPO = 'disabled';
        } else if (role == RoleId.ProjectAdministrator) {
          rights.canManageProjects = 'disabled';
          rights.canAPO = 'disabled';
        } else if (role == RoleId.ReviewManager) {
          rights.canManageProjects = 'disabled';
          rights.canIngestions = 'disabled';
          rights.canAPO = 'disabled';
        } else if (role == RoleId.Reviewer) {
          rights.canManage = 'disabled';
          rights.canManageProjects = 'disabled';
          rights.canIngestions = 'disabled';
          rights.canProductions = 'disabled';
          rights.canProview = 'disabled';
          rights.canDatasets = 'disabled';
          rights.canAllReports = 'disabled';
          rights.canExportToTrueLaw = 'disabled';
          rights.canGenAi = 'disabled';
          rights.canManageIndices = 'disabled';
          rights.canManageProtocols = 'disabled';
          rights.canAIReview = 'disabled';
          rights.canAIBatchSummarize = 'disabled';
          rights.canAIInvestigate = 'disabled';
          rights.canAPO = 'disabled';
        }
      }

      if (role == RoleId.DomainAdministrator) {
        rights.canAPO = 'disabled';
      } else if (editedRole == RoleId.ProjectAdministrator) {
        rights.canManageProjects = 'disabled';
        rights.canAPO = 'disabled';
      } else if (editedRole == RoleId.ReviewManager) {
        rights.canManageProjects = 'disabled';
        rights.canIngestions = 'disabled';
        rights.canAPO = 'disabled';
      } else if (editedRole == RoleId.Reviewer) {
        rights.canManage = 'disabled';
        rights.canManageProjects = 'disabled';
        rights.canIngestions = 'disabled';
        rights.canProductions = 'disabled';
        rights.canProview = 'disabled';
        rights.canDatasets = 'disabled';
        rights.canAllReports = 'disabled';
        rights.canGenAi = 'disabled';
        rights.canManageIndices = 'disabled';
        rights.canManageProtocols = 'disabled';
        rights.canAIReview = 'disabled';
        rights.canAIBatchSummarize = 'disabled';
        rights.canAIInvestigate = 'disabled';
        rights.canAPO = 'disabled';
      }
      setUserRights((prev) => {
        const newState = {...prev, ...rights};
        return isEqual(prev, newState) ? prev : newState;
      });
    }
  });

  const [showToast, setShowToast] = useState<boolean>(false);

  const {
    mutate: editUser,
    isPending: isLoadingEditUser,
    error: editUserError,
  } = useEditUser();

  useEffect(() => {
    if (rolesOptions && editUserRole) {
      const matchedRole =
        rolesOptions.find((r) => r?.key?.toString() === editUserRole) || null;
      setSelectedRole(matchedRole);
    }
  }, [rolesOptions, editUserRole]);

  // Initialize when user changes
  useEffect(() => {
    if (user?.userRights && selectedRole?.key) {
      setUserRights(user.userRights);
    }
  }, [user, selectedRole?.key]);

  // Initialize form when user or security groups change
  useEffect(() => {
    if (!user) return;
    const firstNameValue = user.firstName || '';
    const lastNameValue = user.lastName || '';
    setFirstName(firstNameValue);
    setLastName(lastNameValue);
    setPrimaryEmail(user.emailID || '');
    setEditUserRole(user.role?.toString() || '');
    setIsBillableUser(user.isBillable || false);
    setIsAttorneyProfile(user.isAttorneyProfile || false);
    setIsActive(user.isActive || false);
    setIsLocked(user.isLocked || false);
    setProjectName(user.selectedProject || '');
    setDomainName(user.selectedDomain || '');
    if (firstNameValue) {
      setIsFNameValid(FIRST_LAST_NAME_REGEX.test(firstNameValue));
      setIsFNameMaxLengthValid(firstNameValue.length <= MAX_NAME_LENGTH);
    } else {
      setIsFNameValid(false);
      setIsFNameMaxLengthValid(true);
    }

    if (lastNameValue) {
      setIsLNameValid(FIRST_LAST_NAME_REGEX.test(lastNameValue));
      setIsLNameMaxLengthValid(lastNameValue.length <= MAX_NAME_LENGTH);
    } else {
      setIsLNameValid(false);
      setIsLNameMaxLengthValid(true);
    }
    const selectedGroups = securityGRPsOptions.filter((grp) =>
      user.securityGroupIDs?.includes(grp.key.toString())
    );
    setSecurityGroups(selectedGroups);

    setSaveDisable(true);
    setTouchedFields({fName: false, lName: false});
    setFieldErrors({});
  }, [user, securityGRPsOptions.length]);

  const getRequiredFields = () => {
    const baseFields = {
      fName: firstName,
      lName: lastName,
      primaryEmail,
      selectedRole,
    };
    const roleId = selectedRole?.key?.toString();
    const prevUserRoleId = user?.role?.toString();
    if (
      roleId == RoleId.ProjectAdministrator &&
      prevUserRoleId == RoleId.DomainAdministrator
    ) {
      return {
        ...baseFields,
        project,
      };
    } else if (
      (roleId == RoleId.ReviewManager || roleId == RoleId.Reviewer) &&
      prevUserRoleId == RoleId.DomainAdministrator
    ) {
      return {
        ...baseFields,
        project,
        securityGroups,
      };
    } else if (
      (roleId == RoleId.ReviewManager || roleId == RoleId.Reviewer) &&
      (prevUserRoleId == RoleId.ProjectAdministrator ||
        prevUserRoleId == RoleId.ReviewManager ||
        prevUserRoleId == RoleId.Reviewer)
    ) {
      return {
        ...baseFields,
        securityGroups,
      };
    }

    return baseFields;
  };
  const isFormValid =
    Object.values(getRequiredFields()).every(
      (value) => value?.toString().trim() !== '' && !isEmpty(value)
    ) &&
    isFNameValid &&
    isLNameValid &&
    isFNameMaxLengthValid &&
    isLNameMaxLengthValid;
  // Enable save if any field changes
  useEffect(() => {
    if (!user) return;
    const hasChanged =
      firstName !== user.firstName ||
      lastName !== user.lastName ||
      primaryEmail !== user.emailID ||
      isBillableUser !== user.isBillable ||
      isAttorneyProfile !== user.isAttorneyProfile ||
      isActive !== user.isActive ||
      isLocked !== user.isLocked ||
      selectedRole?.key !== user.role ||
      securityGroups
        .map((g) => g.key)
        .sort()
        .join(',') !== (user.securityGroupIDs || []).sort().join(',');
    const shouldDisable = !hasChanged || !isFormValid;
    setSaveDisable(shouldDisable);
  }, [
    firstName,
    lastName,
    primaryEmail,
    isBillableUser,
    isAttorneyProfile,
    isActive,
    isLocked,
    securityGroups,
    user,
    project,
    selectedRole,
    isFormValid,
  ]);

  const formatUserRights = (
    permissionsData: Record<string, boolean | 'disabled'>
  ) => {
    // eslint-disable-next-line prefer-const
    for (let key in permissionsData) {
      if (permissionsData[key] === 'disabled') {
        permissionsData[key] = false;
      }
    }
    // Native Redaction is hidden for release 1.1.
    if ('canImageRedaction' in permissionsData) {
      permissionsData.canNativeRedaction = permissionsData.canImageRedaction;
    }
    return permissionsData;
  };

  const handleSave = () => {
    const securityGroupIDs = securityGroups.map((item) => String(item.key));
    const permissionsData = {...userRights};
    const editedProject = project?.key || user?.projectID;
    const payload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      emailId: primaryEmail,
      localeCode: 'en-us',
      roleId: selectedRole?.key?.toString(),
      entityId: user?.entityID,
      projectId: editedProject,
      securityGroupId: user?.securityGroupID,
      securityGroupIds: securityGroupIDs,
      isBillable: isBillableUser,
      isActive: isActive,
      isLocked: isLocked,
      ssoEnabled: user?.ssoEnabled,
      isAttorneyProfile: isAttorneyProfile,
      previousRoleID: user?.role.toString(),
      previousProjectID: user?.previousProjectID,
      previousEntityID: user?.previousEntityID,
      userRights: formatUserRights(permissionsData),
      previousFirstName: user?.firstName,
      previousLastName: user?.lastName,
      previousIsActive: user?.isActive,
      previousIsBillable: user?.isBillable,
    };

    editUser(
      {
        userId: userId,
        payload: payload,
      },
      {
        onSuccess: () => {
          setShowToast(true);
          setToastStatuses((prev) => [...prev, ToastType.Success]);
          const validProjectId = Number.isFinite(editedProject)
            ? Number(editedProject)
            : undefined;
          const updatedIds = {userId, projectId: validProjectId, entityId};
          setInitialIds(updatedIds);
          refetch();

          // Trigger grid refresh if callback provided
          if (onUserEdited) {
            onUserEdited();
          }
        },
        onError: () => {
          setShowToast(true);
          setToastStatuses((prev) => [...prev, ToastType.Error]);
        },
      }
    );
    setSaveDisable(true);
  };
  const handleToastClose = () => {
    setShowToast(false);
    setToastStatuses([]);
  };
  const onFirstNameChange = (value: string) => {
    setFirstName(value);
    if (value) {
      const isValid = FIRST_LAST_NAME_REGEX.test(value);
      setIsFNameValid(isValid);
      setIsFNameMaxLengthValid(value.length <= MAX_NAME_LENGTH);
    } else {
      setIsFNameValid(false);
      setIsFNameMaxLengthValid(true);
    }
  };

  const onLastNameChange = (value: string) => {
    setLastName(value);
    if (value) {
      const isValid = FIRST_LAST_NAME_REGEX.test(value);
      setIsLNameValid(isValid);
      setIsLNameMaxLengthValid(value.length <= MAX_NAME_LENGTH);
    } else {
      setIsLNameValid(false);
      setIsLNameMaxLengthValid(true);
    }
  };
  const handleBlur = (field: 'fName' | 'lName') => {
    setTouchedFields((prev) => ({...prev, [field]: true}));
  };
  const tabData: TabData[] = [
    {
      id: 'title-tab',
      label: (
        <Typography
          className="typo-text subhead4_size"
          data-testid={`edit-user-title`}
        >
          {t('userManagement.editUser.details.title')}
        </Typography>
      ),
      content: (
        <DetailsTab
          editUserRole={editUserRole}
          rolesOptions={rolesOptions}
          firstName={firstName}
          lastName={lastName}
          primaryEmail={primaryEmail}
          selectedRole={selectedRole}
          isBillableUser={isBillableUser}
          isAttorneyProfile={isAttorneyProfile}
          isActive={isActive}
          isLocked={isLocked}
          securityGroups={securityGroups}
          setFirstName={onFirstNameChange}
          setLastName={onLastNameChange}
          setPrimaryEmail={setPrimaryEmail}
          setSelectedRole={setSelectedRole}
          setIsBillableUser={setIsBillableUser}
          setIsAttorneyProfile={setIsAttorneyProfile}
          setIsActive={setIsActive}
          setIsLocked={setIsLocked}
          setSecurityGroups={setSecurityGroups}
          securityGRPsOptions={
            securityGRPsOptions.length > 0
              ? securityGRPsOptions
              : securityGRPsOptionsForEdit
          }
          domainName={domainName}
          projectName={projectName}
          projectOptions={userMeta?.projectListSelect ?? []}
          project={project}
          setProject={setProject}
          isFNameValid={isFNameValid}
          isFNameMaxLengthValid={isFNameMaxLengthValid}
          isLNameValid={isLNameValid}
          isLNameMaxLengthValid={isLNameMaxLengthValid}
          fieldErrors={fieldErrors}
          handleBlur={handleBlur}
          touchedFields={touchedFields}
        />
      ),
    },
    {
      id: 'functionality-tab',
      label: (
        <Box className="functionality-tab-label">
          <Typography
            className="typo-text subhead4_size"
            data-testid={`edit-user-functionality-text`}
          >
            {t('userManagement.editUser.functionality.title')}
          </Typography>
          <ArrTooltipWrapper
            trigger={<InfoOutline fontSize="small" className="icon" />}
          >
            {t('userManagement.editUser.functionality.titleInfo')}
          </ArrTooltipWrapper>
        </Box>
      ),
      content: (
        <FunctionalityTab
          permissions={userRights}
          onRightsChange={handleFunctionalityChange}
        />
      ),
    },
  ];

  const contentBody = (
    <Box className="edit-user-drawer">
      <Box className="header-content">
        <Box className="title">
          <Typography
            className="typo-text headline3_size"
            data-testid={`edit-user-title-txt`}
          >
            {t('userManagement.editUser.title')}
          </Typography>
        </Box>
        <Box className="buttons">
          <ArrButton
            variant="secondary"
            title={t('common.cancel')}
            size="medium"
            onClick={handleCancel}
            dataTestId="edit-user-cancel-button"
            className="cancel-button"
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            variant="primary"
            title={t('common.save')}
            size="medium"
            onClick={handleSave}
            dataTestId="edit-user-save-button"
            className="save-button"
            disabled={saveDisable || !isFormValid}
          >
            {t('common.save')}
          </ArrButton>
        </Box>
      </Box>
      <Box className="body-content">
        <Box className="tabs-section">
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="edit user tabs"
            className="tabs"
          >
            {tabData.map((tab) => (
              <Tab
                id={tab.id}
                aria-controls={`tabpanel-${tab.id}`}
                key={tab.id}
                label={tab.label}
                className={`tab ${value === 0 ? 'selected-tab' : 'non-selected-tab'}`}
              />
            ))}
          </Tabs>
          {tabData.map((tab, index) => (
            <TabPanel key={tab.id} value={value} index={index}>
              {tab.content}
            </TabPanel>
          ))}
        </Box>
      </Box>
      {unsavedChangesModal && (
        <ArrModal
          showModal={unsavedChangesModal}
          showClose={false}
          onClose={() => setUnsavedChangesModal(false)}
          title={t('common.unsavedTitle')}
          description={t('common.unsavedDescription')}
          dataTestId="unsaved-changes-modal"
          buttons={[
            {
              id: '1',
              label: t('common.noDontSave'),
              onClick: () => {
                handleReset();
                handleDrawer(false);
                setUnsavedChangesModal(false);
              },
              variant: 'secondary',
              dataTestId: 'ucm-dontsave',
            },
            {
              id: '2',
              label: t('common.saveChanges'),
              onClick: () => {
                handleSave();
                setUnsavedChangesModal(false);
              },
              variant: 'primary',
              dataTestId: 'ucm-save',
            },
          ]}
        />
      )}
    </Box>
  );

  return (
    <>
      {showDrawer && (
        <ArrDrawer
          showButton={true}
          isOpen={showDrawer}
          size="small"
          side="right"
          onClose={() => handleDrawer(false)}
        >
          <div
            className="edit-user-drawer-content"
            data-testid={`edit-user-drawer-div`}
          >
            <Box className="loading-overlay">
              {isLoadingUser || isLoadingUserMeta || isLoadingEditUser ? (
                <ArrLoadingSpinner
                  show={isLoadingUser || isLoadingUserMeta || isLoadingEditUser}
                />
              ) : (
                contentBody
              )}
            </Box>
            {showToast && (
              <ArrToastNotification
                message={
                  toastStatuses[toastStatuses.length - 1] === ToastType.Success
                    ? t('userManagement.editUser.successTostMSG')
                    : (editUserError?.message ?? '')
                }
                onClose={handleToastClose}
                status={toastStatuses}
              />
            )}
          </div>
        </ArrDrawer>
      )}
    </>
  );
};

export default EditUser;
